import axios from 'axios';

interface UserExistsResponse {
  success: boolean;
  exists: boolean;
  uid?: string;
  email?: string;
  displayName?: string;
  photoURL?: string;
  error?: string;
}

interface InvitationUserCheckResponse {
  success: boolean;
  exists: boolean;
  shouldRedirect: 'login' | 'signup';
  userInfo: {
    uid?: string;
    email: string;
    displayName?: string;
    photoURL?: string;
  };
  error?: string;
}

/**
 * Check if a user exists in Firebase Authentication by email
 */
export const checkUserExists = async (email: string): Promise<UserExistsResponse> => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/check-user-exists`, {
      email: email.toLowerCase()
    });

    return response.data;
  } catch (error: any) {
    console.error('Error checking user existence:', error);
    
    // Handle different types of errors
    if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
      return {
        success: false,
        exists: false,
        error: 'Network connection failed. Please check your internet connection and try again.'
      };
    }
    
    if (error.response?.status >= 500) {
      return {
        success: false,
        exists: false,
        error: 'Server error occurred. Please try again later.'
      };
    }
    
    return {
      success: false,
      exists: false,
      error: error.response?.data?.error || error.message || 'Failed to check user existence'
    };
  }
};

/**
 * Check multiple users existence
 */
export const checkUsersExist = async (emails: string[]): Promise<{
  success: boolean;
  users?: Array<{
    email: string;
    exists: boolean;
    uid?: string;
    displayName?: string;
    photoURL?: string;
  }>;
  error?: string;
}> => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/check-users-exist`, {
      emails: emails.map(email => email.toLowerCase())
    });

    return response.data;
  } catch (error: any) {
    console.error('Error checking users existence:', error);
    
    // Handle different types of errors
    if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
      return {
        success: false,
        error: 'Network connection failed. Please check your internet connection and try again.'
      };
    }
    
    if (error.response?.status >= 500) {
      return {
        success: false,
        error: 'Server error occurred. Please try again later.'
      };
    }
    
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to check users existence'
    };
  }
};

/**
 * Check invitation user and determine redirect path
 */
export const checkInvitationUser = async (
  invitationId: string, 
  email?: string
): Promise<InvitationUserCheckResponse> => {
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/auth/invitation-user-check/${invitationId}`;
    const params = email ? { email: email.toLowerCase() } : {};

    const response = await axios.get(url, { params });

    return response.data;
  } catch (error: any) {
    console.error('Error checking invitation user:', error);
    return {
      success: false,
      exists: false,
      shouldRedirect: 'signup',
      userInfo: { email: email || '' },
      error: error.response?.data?.error || error.message || 'Failed to check invitation user'
    };
  }
};

/**
 * Enhanced authentication check for invitation pages
 * This checks both user existence in Firebase Auth AND current authentication status
 */
export const checkAuthAndRedirect = async (
  invitationId: string,
  currentUser: any, // Current authenticated user from context
  email?: string,
  navigate?: (path: string) => void
): Promise<{
  shouldProceed: boolean;
  userExists: boolean;
  redirectPath?: string;
  isAuthenticated: boolean;
}> => {
  try {
    // If user is already authenticated, let them proceed
    if (currentUser) {
      return {
        shouldProceed: true,
        userExists: true,
        isAuthenticated: true
      };
    }

    // If not authenticated, check if user exists in Firebase Auth
    const result = await checkInvitationUser(invitationId, email);
    
    if (!result.success) {
      console.error('Failed to check invitation user:', result.error);
      const fallbackPath = '/signup';
      if (navigate) {
        navigate(fallbackPath);
      }
      return {
        shouldProceed: false,
        userExists: false,
        redirectPath: fallbackPath,
        isAuthenticated: false
      };
    }

    const redirectPath = result.shouldRedirect === 'login' ? '/login' : '/signup';

    // If navigate function is provided and user is not authenticated, redirect
    if (navigate) {
      navigate(redirectPath);
    }

    return {
      shouldProceed: false, // Don't proceed until user is authenticated
      userExists: result.exists,
      redirectPath: redirectPath,
      isAuthenticated: false
    };
  } catch (error: any) {
    console.error('Error in checkAuthAndRedirect:', error);
    
    // Fallback to signup if there's an error
    const fallbackPath = '/signup';
    if (navigate) {
      navigate(fallbackPath);
    }
    
    return {
      shouldProceed: false,
      userExists: false,
      redirectPath: fallbackPath,
      isAuthenticated: false
    };
  }
};

/**
 * Simple helper to check if current user is authenticated
 * This can be used alongside checkAuthAndRedirect for comprehensive auth checking
 */
export const isCurrentUserAuthenticated = (): boolean => {
  // This would typically check Firebase Auth current user
  // But since we want to verify against the backend, we should use the API
  return false; // Always return false to force API check
};
